<script lang="ts" setup>
import { reactive, ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { v4 as uuidv4 } from 'uuid';
import { getFileDownloadUrl } from '@/utils/fileService';
import {
  Attributes_BclToFastq,
  Attributes_BclToFastq_Sample,
} from '@/utils/compute-utils';
import { ComputeTaskCreateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { COMPUTE_TASK_TYPES } from '@/constants';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import { useUserStore } from '@/store/user';
import DeveloperOptions from '@/components/compute/DeveloperOptions.vue';

const userStore = useUserStore();

// --- Component references ---
const devOptions = ref<InstanceType<typeof DeveloperOptions>>();

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  bucketId: number;
  bucketName: string;
  fileBcl: string;
  fileCsv: string | null;
  defaultName: string;
}>();

const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const computeName = ref<string>(props.defaultName);
const customCloudRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);

const csvFileContent = ref<string>('');
const csvFileLoading = ref<boolean>(false);
const csvFileLoadingError = ref<string | null>(null);

const show = computed<boolean>(() => {
  return props.show;
});

// --- sample sheet rows ---
const rows = reactive<Attributes_BclToFastq_Sample[]>([]);

async function initializeRows() {
  rows.splice(0); // clear rows
  if (props.fileCsv) {
    await fetchSampleSheetCsv();
    await parseSampleSheetCsv();
  } else {
    rows.push({ id: '', name: '', index: '', index2: '' });
  }
}
initializeRows();

function addRow() {
  rows.push({ id: '', name: '', index: '', index2: '' });
}
function removeRow(idx: number) {
  rows.splice(idx, 1);
}

function loadSampleSheetCsvFromFile(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files.length === 1) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        csvFileContent.value = e.target?.result as string;
        parseSampleSheetCsv();
      };
      reader.readAsText(file);
    }
  }
}

async function fetchSampleSheetCsv() {
  if (!props.fileCsv) {
    csvFileLoadingError.value = 'No CSV file provided';
    return;
  }

  // Set loading state and clear the content
  csvFileContent.value = '';
  csvFileLoading.value = true;

  // Attempt to fetch the CSV file
  try {
    // Get download URL
    const downloadUrl = await getFileDownloadUrl(props.bucketId, props.fileCsv);
    // Validate URL
    if (!downloadUrl) {
      console.error(
        `Unexpected error fetching download URL for the CSV file ${props.fileCsv}`
      );
      alert(
        'Failed to generate download link. The requested CSV file might not be available anymore.'
      );
      return;
    }
    // Fetch content if preview is embedded
    const response = await fetch(downloadUrl);
    // Validate response
    if (!response.ok) {
      throw new Error('Failed to fetch CSV file content.');
    }
    // Plain text content
    csvFileContent.value = await response.text();
    // Otherwise clear the error
    csvFileLoadingError.value = null;
    console.debug('CSV file content loaded:', csvFileContent.value);
  } catch (err: any) {
    csvFileLoadingError.value = err.message;
  } finally {
    csvFileLoading.value = false;
  }
}

async function parseSampleSheetCsv() {
  if (!csvFileContent.value) {
    csvFileLoadingError.value = 'No CSV file content to parse';
    return;
  }
  csvFileLoading.value = true;
  try {
    // Remove lines before the line starting with [Data], including the line itself and the next line
    // This is because the CSV file may contain metadata or comments before the actual data section
    // The first line afar the [Data] line is the header
    const lines = csvFileContent.value.split('\n');
    const dataStartIndex = lines.findIndex((line) => line.startsWith('[Data]'));
    if (dataStartIndex === -1) {
      throw new Error('No [Data] section found in the provided CSV file.');
    }
    // Ensure there is enough lines after the header
    if (dataStartIndex + 2 >= lines.length) {
      throw new Error('No data found in the provided CSV file.');
    }
    // Extract the data lines after the header
    const dataLines = lines.slice(dataStartIndex + 2);
    // Parse the CSV data
    const parsedData = dataLines
      .map((line) => line.split(','))
      .filter((line) => line.length >= 4)
      .map((line) => ({
        id: line[0].trim(),
        name: line[1].trim(),
        index: line[2].trim(),
        index2: line[3].trim(),
      }));
    // Clear existing rows and add parsed data
    rows.splice(0); // clear rows
    parsedData.forEach((row) => {
      rows.push({
        id: row.id,
        name: row.name,
        index: row.index,
        index2: row.index2,
      });
    });
    // Check if the CSV file is empty
    if (rows.length === 0) {
      throw new Error('CSV file is empty or invalid.');
    }
    // Otherwise clear the error
    csvFileLoadingError.value = null;
    console.debug('CSV file parsed successfully:', rows);
  } catch (err: any) {
    csvFileLoadingError.value = err.message;
  } finally {
    if (rows.length === 0) {
      addRow();
    }
    csvFileLoading.value = false;
  }
}

const sampleMatrixFields = [
  {
    key: 'id',
    label: 'Sample ID',
    tdClass: 'align-middle',
    sortable: true,
  },
  {
    key: 'name',
    label: 'Sample Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  { key: 'index', label: 'i7 Index', sortable: true, tdClass: 'align-middle' },
  {
    key: 'index2',
    label: 'Reverse i5 Index',
    sortable: true,
    tdClass: 'align-middle',
  },
  { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
];

// --- ComputeTaskCreate Mutation ---
const mutationError = ref<string | null>(null);
const mutationInProgress = ref<boolean>(false);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ComputeTaskCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.computeTaskCreate) {
    console.debug(
      'BCL to FASTQ conversion started:',
      res.data.computeTaskCreate
    );
    mutationShowSuccess.value = true;
    setTimeout(() => {
      emits('closeAndRefresh');
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'BCL to FASTQ conversion start failed: ' + res.data;
    console.error('BCL to FASTQ conversion start failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error starting BCL to FASTQ conversion:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function mutationRequested() {
  if (devOptions.value?.extraAttributesInvalid) {
    mutationError.value =
      'Developer JSON attributes must be a valid JSON object.';
    return;
  }

  // check rows do not contain any non-alphanumeric characters (incl spaces)
  for (const row of rows) {
    if (!/^[a-zA-Z0-9-]+$/.test(row.id)) {
      mutationError.value =
        'Sample ID contains invalid characters. Use alphanumeric characters and dashes only.';
      console.error('Sample ID contains invalid characters');
      return;
    }
    if (!/^[a-zA-Z0-9-]+$/.test(row.name)) {
      mutationError.value =
        'Sample name contains invalid characters.  Use alphanumeric characters and dashes only.';
      console.error('Sample name contains invalid characters');
      return;
    }
  }

  // initialize progress values
  mutationInProgress.value = true;
  mutationError.value = null;
  mutationShowSuccess.value = false;

  // initialize attributes
  let attributes: Attributes_BclToFastq = {
    gceRequestId: uuidv4(),
    bucketId: props.bucketId,
    bucketName: props.bucketName,
    fileBcl: props.fileBcl,
    samples: rows,
  };

  // add developer options
  if (userStore.developer && devOptions.value?.expanded) {
    // general - version
    if (devOptions.value.version) {
      attributes.version = devOptions.value.version;
    }

    // general - vm lifecycle
    if (devOptions.value.vmLifecycle) {
      attributes.vmLifecycle = devOptions.value.vmLifecycle;
    }

    // general - machine type
    if (devOptions.value.machineType) {
      attributes.machineType = devOptions.value.machineType;
    }

    // extra attributes from JSON
    if (devOptions.value.extraAttributes) {
      Object.assign(attributes, devOptions.value.extraAttributes);
    }
  }

  console.debug(
    'New BCL to FASTQ conversion requested with attributes:',
    attributes
  );

  // mutationInProgress.value = false;
  executeMutation({
    name: computeName.value,
    bucketId: props.bucketId,
    taskType: COMPUTE_TASK_TYPES.BCL_TO_FASTQ,
    attributes: attributes,
    requestedRegionPrefix: customCloudRegionPrefix.value
      ? customCloudRegionPrefix.value
      : undefined,
  });
}

function onModalHide(evt: Event) {
  // prevent closing modal if mutation is in progress
  if (mutationInProgress.value) {
    evt.preventDefault();
    return;
  }
  // otherwise
  emits('close');
}
</script>
<template>
  <div>
    <b-modal
      v-model="show"
      @hide="onModalHide"
      :backdrop="mutationInProgress ? 'static' : true"
      :no-close-on-esc="mutationInProgress"
      class="modal-lg"
    >
      <!-- Modal Title -->
      <template #title>
        <font-awesome-icon icon="shuffle" class="me-2" />
        Start new BCL to FASTQ conversion
      </template>
      <!-- Modal Content -->
      <div>
        <!-- Sample Matrix -->
        <BFormGroup class="mt-3">
          <BFormInput
            v-model="computeName"
            id="task-name"
            name="task-name"
            placeholder="Enter conversion task name"
            required
          ></BFormInput>
        </BFormGroup>

        <!-- Sample Matrix -->
        <BFormGroup class="mt-3">
          <BTable
            :items="rows"
            :fields="sampleMatrixFields"
            :busy="csvFileLoading"
            small
            responsive
            class="browser-table"
          >
            <template #cell(id)="data">
              <BFormInput v-model="rows[data.index].id" size="sm" />
            </template>
            <template #cell(name)="data">
              <BFormInput v-model="rows[data.index].name" size="sm" />
            </template>
            <template #cell(index)="data">
              <BFormInput v-model="rows[data.index].index" size="sm" />
            </template>
            <template #cell(index2)="data">
              <BFormInput v-model="rows[data.index].index2" size="sm" />
            </template>
            <template #cell(actions)="data">
              <BButton
                :disabled="rows.length <= 1"
                variant="outline-danger"
                size="sm"
                @click="removeRow(data.index)"
                title="Remove sample"
              >
                <font-awesome-icon icon="trash" />
              </BButton>
            </template>
          </BTable>

          <BButton
            variant="outline-primary"
            size="sm"
            @click="addRow"
            class="me-2"
          >
            <font-awesome-icon icon="plus" class="me-2" />
            Add sample
          </BButton>

          <BButton tag="label" variant="outline-primary" size="sm" class="me-2">
            <font-awesome-icon icon="file-csv" class="me-2" />
            Load samples from CSV
            <BFormInput
              type="file"
              accept=".csv"
              ref="fileInput"
              @change="loadSampleSheetCsvFromFile"
              class="d-none"
            />
          </BButton>
        </BFormGroup>

        <!-- CSV Parse Error Message -->
        <b-alert
          v-model="csvFileLoadingError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="csvFileLoadingError = null"
        >
          {{ csvFileLoadingError }}
        </b-alert>

        <!-- Developer options -->
        <DeveloperOptions
          v-if="userStore.developer"
          ref="devOptions"
          size="sm"
          class="mt-3"
        />

        <!-- Cloud Region -->
        <CloudRegionSelection
          v-model="customCloudRegionPrefix"
          v-model:modelRequired="customCloudRegionRequired"
          class="mt-3"
        />

        <!-- GQL Mutation Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>

      <!-- Modal Footer with Action Buttons -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="onModalHide"
          :disabled="mutationInProgress"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="mutationRequested"
          :disabled="mutationInProgress || mutationShowSuccess"
        >
          <span v-if="mutationInProgress">
            Starting Conversion
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Conversion Started
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Start Conversion </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
